import {
    BroadcastResponse as CoreBroadcastResponse,
    StreamSettingResponse
} from "@switcherstudio/api-core-client";
import { ComponentMap } from "components/utility/ComponentMap";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { Link } from "components/buttons/Link";
import { Button } from "components/buttons/Button";
import { PlatformIcon } from "components/icons/PlatformIcon";
import { getChannelName } from "helpers/streamSettings";
import { useCallback, useMemo, useState } from "react";
import { AddDestinationsModal } from "components/modal/AddDestinationsModal";
import { AttentionModal } from "components/modal/AttentionModal";
import { useDispatch } from "react-redux";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { useRemoveDestination } from "hooks/useRemoveDestination";

export interface ExternalDestinationsProps {
    destinations?: StreamSettingResponse[];
    filteredDestinations?: string[];
    // Omit to hide the "Add destinations" button.
    broadcast?: CoreBroadcastResponse;
    // Refetches the broadcast so newly connected destinations show up without a page reload.
    refetchBroadcast?: () => void;
}

export const ExternalDestinations = ({
    destinations,
    filteredDestinations = [
        "VideoPlayerSwitcherStreamSetting",
        "SimulcastSwitcherStreamSetting"
    ],
    broadcast,
    refetchBroadcast
}: ExternalDestinationsProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isAddDestinationsModalOpen, setIsAddDestinationsModalOpen] =
        useState(false);

    const { removeDestination, removingIds } =
        useRemoveDestination(refetchBroadcast);

    const localDestinations = useMemo(() => {
        return destinations?.filter(
            (destination) =>
                !filteredDestinations.includes(
                    destination.Details.Discriminator
                )
        );
    }, [destinations, filteredDestinations]);

    const canAddDestinations = !!broadcast;

    // Outputs are in use once the stream starts - removing one mid-broadcast would
    // drop the feed rather than cancel a pending destination.
    const canRemoveDestinations =
        !!broadcast &&
        (broadcast?.Details?.BroadcastStatus === "Created" ||
            broadcast?.Details?.BroadcastStatus === "Ready");

    const confirmRemoveDestination = useCallback(
        (destination: StreamSettingResponse) => {
            const closeModal = () => dispatch(closeCurrentModal());

            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeModal}
                            handleCancel={closeModal}
                            hasContinueButton={true}
                            continueText={t("buttons:remove")}
                            handleContinue={() =>
                                removeDestination(
                                    broadcast?.Details?.Id,
                                    destination
                                )
                            }
                        >
                            <div>
                                {t("livestreams-page:remove-destination-msg")}
                            </div>
                        </AttentionModal>
                    )
                })
            );
        },
        [broadcast?.Details?.Id, dispatch, removeDestination, t]
    );

    if (!localDestinations?.length && !canAddDestinations) return null;

    return (
        <div>
            <div className={styles["header"]}>
                <h6>{t("livestreams-page:external-destinations")}</h6>
            </div>
            {localDestinations?.length ? (
                <>
                    <ComponentMap
                        items={localDestinations}
                        element={(destination) => (
                            <div className={styles["destination"]}>
                                <div className={styles["destination-channel"]}>
                                    <PlatformIcon
                                        destination={
                                            destination.Details?.Discriminator
                                        }
                                        useNewIcon
                                    />
                                    <Link
                                        to={destination?.Details?.PermalinkUrl}
                                        disabled={
                                            !destination?.Details?.PermalinkUrl
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {getChannelName(destination)}
                                    </Link>
                                </div>
                                {canRemoveDestinations && (
                                    <Button
                                        type="remove"
                                        disabled={
                                            !!removingIds[
                                                destination?.Details
                                                    ?.SwitcherStreamSettingId
                                            ]
                                        }
                                        onClick={() =>
                                            confirmRemoveDestination(
                                                destination
                                            )
                                        }
                                    >
                                        {t("buttons:remove")}
                                    </Button>
                                )}
                            </div>
                        )}
                    />
                </>
            ) : (
                <p className={styles["no-destinations"]}>
                    {t("livestreams-page:no-external-destinations")}
                </p>
            )}
            {canAddDestinations && (
                <Button
                    type="secondary"
                    additionalClassName={styles["add-destinations-button"]}
                    onClick={() => setIsAddDestinationsModalOpen(true)}
                >
                    {t("livestreams-page:add-destinations")}
                </Button>
            )}
            {isAddDestinationsModalOpen && (
                <AddDestinationsModal
                    broadcast={broadcast}
                    onClose={() => {
                        setIsAddDestinationsModalOpen(false);
                        refetchBroadcast?.();
                    }}
                    refetchBroadcast={refetchBroadcast}
                />
            )}
        </div>
    );
};
